/**
 * What the last pull wrote, as one sentence.
 *
 * A pull writes, so the question after pressing it is whether anything moved.
 * The apply records that already -- the sync returns a stats block per kind of
 * row, and it is stored on the record -- so this costs a read rather than a dry
 * run, and it survives a reload the way a toast would not.
 *
 * Counts only. The apply result also carries every field it changed on a row
 * that already existed, which is forty lines of "this physician's NPI went from
 * X to Y" in front of somebody who asked a yes-or-no question.
 *
 * "Changed nothing" is worth as much as the counts: it is the answer the dry run
 * used to give before a fill, now given for free after one.
 *
 * Every block is read leniently, because the stored result is JSONB: a malformed
 * count reads as zero rather than throwing inside a render. What it must not do
 * is then call that "Changed nothing" -- an unreadable result and an empty one
 * are different answers, and only one of them is a fact.
 */

/** The stats blocks the sync returns, in the order an operator reads them. */
const ENTITIES: ReadonlyArray<readonly [string, string, string]> = [
  // [key on the result, singular, plural]
  ['clinics', 'clinic', 'clinics'],
  ['insurances', 'payer', 'payers'],
  ['insurance_visit_rates', 'visit rate', 'visit rates'],
  ['physicians', 'physician', 'physicians'],
  ['staff', 'staff member', 'staff'],
  ['internal_referral_sources', 'referral source', 'referral sources'],
];

function count(block: unknown, key: string): number {
  if (!block || typeof block !== 'object') return 0;
  const value = (block as Record<string, unknown>)[key];
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : 0;
}

/**
 * Whether any block holds a count this can read.
 *
 * A stored result whose every count is missing or malformed is unreadable, not
 * empty: a pull that genuinely changed nothing still writes zeroes.
 */
function readable(result: Record<string, unknown>): boolean {
  return ENTITIES.some(([entity]) => {
    const block = result[entity];
    if (!block || typeof block !== 'object') return false;
    return (['created_count', 'updated_count', 'deleted_count'] as const).some(
      key => {
        const value = (block as Record<string, unknown>)[key];
        return typeof value === 'number' && Number.isFinite(value);
      }
    );
  });
}

function phrase(
  result: Record<string, unknown>,
  key: 'created_count' | 'updated_count' | 'deleted_count'
): string {
  const parts = ENTITIES.map(([entity, one, many]) => {
    const rows = count(result[entity], key);
    return rows ? `${rows.toLocaleString()} ${rows === 1 ? one : many}` : '';
  }).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** "Added 3 physicians and 1 payer, changed 2 physicians." */
export function applySummary(applyResult: unknown): string {
  const result = applyResult as Record<string, unknown> | null | undefined;
  if (!result || typeof result !== 'object') return '';
  const clauses = (
    [
      ['Added', 'created_count'],
      ['changed', 'updated_count'],
      ['removed', 'deleted_count'],
    ] as const
  )
    .map(([verb, key]) => {
      const rows = phrase(result, key);
      return rows ? `${verb} ${rows}` : '';
    })
    .filter(Boolean);
  if (clauses.length === 0) {
    return readable(result)
      ? 'Changed nothing.'
      : 'That fill did not record what it changed.';
  }
  // The first clause leads the sentence, so it is the one that is capitalised;
  // a run with no creations starts with "Changed".
  const [first, ...rest] = clauses;
  const sentence = [first[0].toUpperCase() + first.slice(1), ...rest].join(
    ', '
  );
  return `${sentence}.`;
}
